import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Flex
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import SearchTable2 from "../../../components/Tables/SearchTable2";
import { printNPsService } from "../../../services/printNPsService";
import Loading from "components/Loading/Loading";
import { getEmptyPageResponse, toTablePageableData } from "utils/utils";
import { FaFile, FaFilePdf, FaPaperPlane, FaPrint, FaNewspaper } from "react-icons/fa";
import { useCustomToast } from "components/CustomToast/CustomToast";

const PrintNPsList = () => {
  const { t } = useTranslation();
  const [response, setResponse] = useState(undefined);
  const [isLoadingPrint, setIsLoadingPrint] = useState(false);
  const [isLoadingResponseFile, setIsLoadingResponseFile] = useState(false);
  const [searchParams, setSearchParams] = useState({});
  const showToast = useCustomToast();
  const [includeSentBatches, setIncludeSentBatches] = useState(false);

  const columns = useMemo(() => ([
    {
      Header: t("print-nps.list.table.header.name"),
      accessor: "nome"
    },
    {
      Header: t("print-nps.list.table.header.nps-printing"),
      accessor: "data_impressao_notificacoes_penalidade",
      custom: ({ value }) => {
        if (!value) { return t('print-nps.list.table.labels.no-print'); }
      }
    },
    {
      Header: t("print-nps.list.table.header.nps-sent"),
      accessor: "data_envio_notificacoes_penalidade",
      custom: ({ value }) => {
        if (!value) { return t('print-nps.list.table.labels.no-sent'); }
      }
    },
    {
      Header: t("print-nps.list.table.header.printing-after-date"),
      accessor: "data_limite_impressao_notificacoes_penalidade",
      custom: ({ value }) => {
        if (!value) { return "-"; }
      }
    },
    {
      Header: t('print-nps.list.table.header.actions'),
      disableSortBy: true,
      actions: [
        {
          label: t('print-nps.list.table.actions.print-np'),
          onClick: (row) => handlePrintNp(row.original.id),
          icon: <FaPrint />
        },
        {
          label: t('print-nps.list.table.actions.response-file'),
          onClick: (row) => handleResponseFile(row.original.id),
          icon: <FaFile />
        },
        {
          label: t('print-nps.list.table.actions.confirm-shipping'),
          onClick: (row) => handleConfirmShipping(row.original.id),
          icon: <FaPaperPlane />
        },
        {
          label: t('print-nps.list.table.actions.report'),
          onClick: (row) => handleBatchReportNP(row.original.id),
          icon: <FaFilePdf />
        },
        {
          label: t('print-nas.list.table.actions.report'),
          onClick: (row) => handleEditalBatchReportNP(row.original.id),
          icon: <FaNewspaper />,
          visible: (row) => row.edital_action
        }
      ]
    }
  ]), [includeSentBatches, searchParams]);

  useEffect(() => {
    fetchData();
  }, [includeSentBatches]);

  const handlePrintNp = async (id) => {
    try {
      setIsLoadingPrint(true);

      const { data, headers } = await printNPsService.printNp(id);
      const fileName = headers['content-disposition'].match(/filename="(.+)"/)[1];

      const url = URL.createObjectURL(new Blob([data]));
      Object.assign(document.createElement('a'), {
        href: url,
        download: fileName
      }).click();

      URL.revokeObjectURL(url);
    } catch (error) {
      showToast({ title: t(error), status: "error" });
    } finally {
      setIsLoadingPrint(false);
      fetchData(searchParams);
    }
  };

  const handleBatchReportNP = async (id) => {
      setIsLoadingResponseFile(true);
      try {
        const { data, headers } = await printNPsService.getReportNp(id);
        const filename = headers['content-disposition'].match(/filename="(.+)"/)[1];
  
        const url = URL.createObjectURL(new Blob([data]));
        Object.assign(document.createElement('a'), {
          href: url,
          download: filename
        }).click();
  
        URL.revokeObjectURL(url);
        showToast({ title: t("print-nps.messages.success.details-file-np"), status: "success" });
      } catch (error) {
        showToast({ title: t(error), status: "error" });
      } finally {
        setIsLoadingResponseFile(false);
        fetchData(searchParams);
      }
    };

  const handleEditalBatchReportNP = async (id) => {
    setIsLoadingResponseFile(true);
    try {
      const { data, headers } = await printNPsService.getReportNp(id);
      const filename = headers['content-disposition'].match(/filename="(.+)"/)[1];

      const url = URL.createObjectURL(new Blob([data]));
      Object.assign(document.createElement('a'), {
        href: url,
        download: filename
      }).click();

      URL.revokeObjectURL(url);
      showToast({ title: t("print-nps.messages.success.edital-file-np"), status: "success" });
    } catch (error) {
      showToast({ title: t(error), status: "error" });
    } finally {
      setIsLoadingResponseFile(false);
      fetchData();
    }
  };

  const handleResponseFile = async (id) => {
    try {
      setIsLoadingResponseFile(true);

      const { data, headers } = await printNPsService.getResponseFileNp(id);
      const fileName = headers['content-disposition'].match(/filename="(.+)"/)[1];

      const url = URL.createObjectURL(new Blob([data]));
      Object.assign(document.createElement('a'), {
        href: url,
        download: fileName
      }).click();

      URL.revokeObjectURL(url);
    } catch (error) {
      showToast({ title: t(error), status: "error" });
    } finally {
      setIsLoadingResponseFile(false);
    }
  };

  const handleConfirmShipping = async (id) => {
    try {
      await printNPsService.confirmShippingNp(id);
      showToast({ title: t("print-nps.messages.success.confirm-shipping-np"), status: "success" });
      fetchData(searchParams);
    } catch (error) {
      showToast({ title: t(error), status: "error" });
    }
  };

  const fetchData = async (params = {}) => {
    const finalParams = {
      orderBy: params.orderBy ?? "data_limite_impressao_notificacoes_penalidade",
      orderDirection: params.orderDirection ?? "asc",
      ...params,
      filters: { incluirEnviados: includeSentBatches ? "true" : "false" },
    };

    setSearchParams(finalParams);
    setResponse(undefined);

    try {
      return setResponse(await printNPsService.list(finalParams));
    } catch (err) {
      setResponse(getEmptyPageResponse(finalParams));
      showToast({ title: t(err), status: "error" });
    }
  };

  return (
    <Flex direction="column" gap="5" pt={{ base: "150px", lg: "90px" }}>
      <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
        <ListPageCardHeader title={t("print-nps.title")} subtitle={t("print-nps.description")} />
        <CardBody>
          {response ? (
            <Box position="relative">
              <SearchTable2
                columnsData={columns}
                tableData={response.content}
                pageableData={toTablePageableData(response, { defaultField: "data_limite_impressao_notificacoes_penalidade", ...searchParams })}
                onChange={fetchData}
                includeSentBatches={{
                  value: includeSentBatches,
                  onChange: setIncludeSentBatches
                }}
              />
              {(isLoadingPrint || isLoadingResponseFile) && (
                <Box
                  position="fixed"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  bg="rgba(255, 255, 255, 0.6)"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  zIndex="overlay"
                >
                  <Loading maxH={{ sm: "100px", md: "350px" }} />
                </Box>
              )}
            </Box>
          ) : (
            <Loading maxH={{ sm: "100px", md: "350px" }} />
          )}
        </CardBody>
      </Card>
    </Flex>
  );
}

export default PrintNPsList;
